// Multi Lead Form spec §5.3. Pure helper used everywhere the UI needs to
// know whether a row's lead form selection is still backed by an entry in
// the assigned Campaign's library, or has been orphaned by a Campaign-side
// delete since the row snapshotted.
//
// Returns one of three states:
//
//   - { state: 'unassigned', leadForm: null }
//       Row has no Campaign (rowType === 'unassigned' / 'opt_out', or
//       campaignId set but campaign object unavailable to the caller).
//       Nothing to render in the Lead Form section / cell.
//
//   - { state: 'ok',     leadForm: <CampaignLeadForm> }
//       The row's leadFormId still matches one of campaign.campaignLeadForms.
//       Helper returns the CURRENT Campaign-side entry (not the row's frozen
//       snapshot) so the master grid and Build View show the live name —
//       drift between the row's snapshot and the Campaign's authoring is
//       handled by the diff helper, not here.
//
//   - { state: 'orphan', leadForm: <row snapshot or null> }
//       The row's leadFormId no longer exists in the Campaign's library.
//       Returned leadForm is the row's frozen snapshot (never null when a
//       campaign is assigned with a real leadForm snapshot) so the
//       downstream renders keep working until the AM reassigns. Warning
//       affordance shows up in cells / sections that handle this state
//       (master grid Lead Form cell, Build View, row editor).
//
// Pure: no React, no Firestore — easy to unit-test against fixtures.

import type { AccountCampaign } from '../types/accountCampaign';
import type { Campaign, CampaignLeadForm } from '../types/campaign';

export type ResolvedLeadForm =
  | { state: 'unassigned'; leadForm: null }
  | { state: 'ok'; leadForm: CampaignLeadForm }
  | { state: 'orphan'; leadForm: CampaignLeadForm | null };

export function resolveLeadForm(
  row: Pick<AccountCampaign, 'rowType' | 'leadFormId' | 'leadForm'>,
  campaign: Pick<Campaign, 'campaignLeadForms'> | null,
): ResolvedLeadForm {
  if (row.rowType !== 'campaign' || !campaign) {
    return { state: 'unassigned', leadForm: null };
  }
  const list = campaign.campaignLeadForms ?? [];
  if (row.leadFormId === null) {
    // Campaign-type row without a selected lead form id is unusual but not
    // impossible during transitions. Treat as orphan so the cell prompts
    // the AM to pick one; row.leadForm may also be null in this state.
    return { state: 'orphan', leadForm: row.leadForm };
  }
  const live = list.find((entry) => entry.id === row.leadFormId);
  if (live) {
    return { state: 'ok', leadForm: live };
  }
  return { state: 'orphan', leadForm: row.leadForm };
}
